import instance, { ADMIN_PREFIX } from 'services/api';

/**
 * Prompt assistant-category (BE AI_THREAD_MESSAGE_TYPE). The BE requires
 * `type` on create; it is one of these four categories (NOT the OpenAI
 * message roles it briefly used).
 */
export const AI_PROMPT_TYPES = [
  'medical',
  'general',
  'specialized',
  'custom'
] as const;

export type AiPromptType = (typeof AI_PROMPT_TYPES)[number];

/** antd Select options for the prompt category (shared by add + edit UIs). */
export const AI_PROMPT_TYPE_OPTIONS = AI_PROMPT_TYPES.map(value => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1)
}));

export interface AiPrompt {
  id: string;
  message: string;
  type?: AiPromptType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

/**
 * GET /prompts — list of prompt templates used by the AiHealth
 * admin panel (BE controller: @Controller('prompts')).
 */
export const fetchAiPrompts = async (): Promise<AiPrompt[]> => {
  const res = await instance.get<{ data: AiPrompt[] }>(
    `${ADMIN_PREFIX}/prompts`
  );

  return res?.data?.data ?? [];
};

/**
 * POST /prompts — adds a new prompt template. `type` (assistant-category)
 * is required by the BE.
 */
export const createAiPrompt = (message: string, type: AiPromptType) =>
  instance.post(`${ADMIN_PREFIX}/prompts`, { message, type });

/**
 * PUT /prompts/:id — replace the message (and optionally the category) of
 * an existing template.
 */
export const updateAiPrompt = (
  id: string,
  message: string,
  type?: AiPromptType
) =>
  instance.put(`${ADMIN_PREFIX}/prompts/${id}`, {
    message,
    ...(type ? { type } : {})
  });

/**
 * DELETE /prompts/:id — soft-remove a template.
 */
export const deleteAiPrompt = (id: string) =>
  instance.delete(`${ADMIN_PREFIX}/prompts/${id}`);
